import Vue from 'vue';
import store from '@/store';
import router from '@/router';
import axios from 'axios';
import VueAxios from 'vue-axios';
const camelcaseObjectDeep = require('camelcase-object-deep');

Vue.use(VueAxios, axios);

const TOKEN_STORAGE_KEY = 'user_token';
const REFRESH_TOKEN_URL = 'v1/auth/refresh';
const REFRESH_BEFORE_EXPIRATION_MS = 5 * 60 * 1000;
const DEFAULT_TOKEN_LIFETIME_MS = 60 * 60 * 1000;

let refreshTimeoutId = null;
let refreshPromise = null;

export const $http = axios.create({
  baseURL: process.env.VUE_APP_ROOT_API,
  headers: {
    'Authorization': `Bearer ${localStorage.getItem(TOKEN_STORAGE_KEY)}`,
    'Accept': 'application/json',
  }
});

const refreshHttp = axios.create({
  baseURL: process.env.VUE_APP_ROOT_API,
  headers: {
    'Accept': 'application/json',
  }
});

const getToken = () => localStorage.getItem(TOKEN_STORAGE_KEY);

const decodeJwtPayload = (token) => {
  if (!token) {
    return null;
  }

  const [, payload] = token.split('.');

  if (!payload) {
    return null;
  }

  try {
    const normalizedPayload = payload.replace(/-/g, '+').replace(/_/g, '/');
    const paddedPayload = normalizedPayload.padEnd(
      normalizedPayload.length + (4 - normalizedPayload.length % 4) % 4,
      '='
    );
    const decodedPayload = decodeURIComponent(
      atob(paddedPayload)
        .split('')
        .map((char) => `%${(`00${char.charCodeAt(0).toString(16)}`).slice(-2)}`)
        .join('')
    );

    return JSON.parse(decodedPayload);
  } catch (e) {
    return null;
  }
};

export const clearTokenRefreshTimeout = () => {
  if (refreshTimeoutId) {
    clearTimeout(refreshTimeoutId);
    refreshTimeoutId = null;
  }
};

export const scheduleTokenRefresh = (token = getToken()) => {
  clearTokenRefreshTimeout();

  if (!token) {
    return;
  }

  const payload = decodeJwtPayload(token);
  const expiresAtMs = payload && payload.exp ? payload.exp * 1000 : Date.now() + DEFAULT_TOKEN_LIFETIME_MS;
  const refreshInMs = Math.max(expiresAtMs - Date.now() - REFRESH_BEFORE_EXPIRATION_MS, 0);

  refreshTimeoutId = setTimeout(() => {
    refreshToken().catch(() => {
      handleUnauthorizedSession();
    });
  }, refreshInMs);
};

const applyToken = (token) => {
  store.commit('auth/SET_TOKEN', token);
};

const handleUnauthorizedSession = () => {
  store.commit('auth/REMOVE_TOKEN');
  router.push({name: 'sign-in'}).catch(() => {console.log()});
};

const extractAccessToken = (response) => {
  const data = response && response.data ? response.data : {};

  return data.accessToken || data.access_token || data.token || null;
};

export const refreshToken = async () => {
  if (refreshPromise) {
    return refreshPromise;
  }

  const currentToken = getToken();

  if (!currentToken) {
    return Promise.reject(new Error('No token available to refresh'));
  }

  refreshPromise = refreshHttp.post(REFRESH_TOKEN_URL, null, {
    headers: {
      'Authorization': `Bearer ${currentToken}`,
      'X-localization': store.getters.currentLanguage.translationKey,
    }
  }).then((response) => {
    const token = extractAccessToken(response);

    if (!token) {
      throw new Error('Refresh response does not include an access token');
    }

    applyToken(token);

    return token;
  }).finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
};

$http.interceptors.request.use((config) => {
  config.headers['Authorization'] = `Bearer ${getToken()}`;
  config.headers['X-localization'] = store.getters.currentLanguage.translationKey;
  return config;
});
$http.interceptors.response.use((response) => {
    response.data = camelcaseObjectDeep(response.data);
    return response;
  }, async (error) => {
    const response = error.response;

    if (!response) {
      return Promise.reject(error);
    }

    switch (response.status) {
      case 500: {
        router.push({name: 'server-not-found'}).catch(() => {console.log()});
        break;
      }
      case 404: {
        router.push({name: 'page-not-found'}).catch(() => {console.log()});
        break;
      }
      case 401: {
        const originalRequest = error.config;

        if (getToken() && originalRequest && !originalRequest._retry) {
          originalRequest._retry = true;

          try {
            const token = await refreshToken();
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers['Authorization'] = `Bearer ${token}`;

            return $http(originalRequest);
          } catch (e) {
            handleUnauthorizedSession();
          }
        } else {
          handleUnauthorizedSession();
        }

        break;
      }
      default: {
        break;
      }
    }
  return Promise.reject(camelcaseObjectDeep(response));
  }
);

export default function install(Vue) {
  Object.defineProperty(Vue.prototype, '$http', {
    get() {
      return $http
    }
  })
}
